import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react'

import { clampSplitHeight, documentationSplitHeight, splitAvailable, type SplitMetrics } from './splitHeight'

const splitMetrics = (workspace: HTMLElement, divider: HTMLElement): SplitMetrics => {
  const style = getComputedStyle(workspace)
  return {
    workspaceHeight: workspace.getBoundingClientRect().height,
    paddingTop: Number.parseFloat(style.paddingTop) || 0,
    paddingBottom: Number.parseFloat(style.paddingBottom) || 0,
    gap: Number.parseFloat(style.rowGap || style.gap) || 0,
    dividerHeight: divider.offsetHeight,
  }
}

export const useSplitPane = (height: number | null, setHeight: (height: number) => void) => {
  const workspaceRef = useRef<HTMLDivElement>(null)
  const dividerRef = useRef<HTMLDivElement>(null)
  const stopDrag = useRef<(() => void) | null>(null)

  useEffect(() => {
    const workspace = workspaceRef.current
    const divider = dividerRef.current
    if (!workspace || !divider || height == null) return
    const observer = new ResizeObserver(() => {
      if (workspace.clientHeight === 0) return
      const next = clampSplitHeight(height, splitAvailable(splitMetrics(workspace, divider)))
      if (next !== height) setHeight(next)
    })
    observer.observe(workspace)
    return () => observer.disconnect()
  }, [height, setHeight])

  useEffect(() => () => stopDrag.current?.(), [])

  const onDividerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const workspace = workspaceRef.current
    const divider = dividerRef.current
    if (!workspace || !divider) return
    stopDrag.current?.()
    const move = (moveEvent: PointerEvent) =>
      setHeight(
        documentationSplitHeight({
          pointerY: moveEvent.clientY,
          workspaceTop: workspace.getBoundingClientRect().top,
          ...splitMetrics(workspace, divider),
        })
      )
    const end = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      stopDrag.current = null
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    stopDrag.current = end
  }

  return { workspaceRef, dividerRef, onDividerDown }
}
